import React from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";
import { Item } from "../../models/item";
import { SummaryItem } from "../../models/summaryItem";
import { CLEAR_PREVIEW_ITEM, PREVIEW_ITEM, REMOVE_ITEM } from "../../store/actions/configurationActions";
import { TRIGGER_PREFERRED_ANGLE } from "../../store/actions/viewActions";
import { getItemStates, getPreviewItem, getShowPrice, getSummaryItems } from "../../store/sagas/selectors";
import GaReporter from "../../utilities/gaReporter";
import { useBreakpoint } from "../../utilities/media-js-breakpoints";
import { usePriceFormatter } from "../../utilities/priceFormatter";
import { useText } from "../../utilities/textUtil";
import CustomScrollbar from "../CustomScrollbar/CustomScrollbar";

const ItemContainer = styled.div`
    height: 100%;
    width: 100%;
`;

const Items = styled.div`
    display: flex;
    flex-direction: column;
    padding: 25px 24px 0 14px;
    ${({ theme }) => theme.bp.landscape`
        padding: 25px 16px 0 6px;
    `}
    ${({ theme }) => theme.bp.md`
        padding: 25px 24px 0 14px;
    `}
    width: 100%;
    box-sizing: border-box;
`;

const ItemName = styled.div`
    margin-right: 10px;
    width: 70%;
    font-weight: 500;
    font-size: 16px;
    cursor: pointer;
    padding: 16px 0;
    ${({ theme }) => theme.bp.landscape`
        padding: 0;
        font-size: 13px;
    `}
    ${({ theme }) => theme.bp.sm`
        margin-right: 20px;
    `}
    flex-grow: 1;
    flex-shrink: 1;
`;

const EyeIcon = styled.span`
    flex-basis: 24px;
    font-size: 16px;
    visibility: hidden;
    &.visible {
        visibility: visible;
    }
    flex-grow: 0;
`;

const ItemPrice = styled.div`
    flex: 1;
    text-align: right;
    white-space: nowrap;
    margin-right: 5px;
`;

const ItemRow = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;
    padding: 0 10px;
    margin-bottom: 10px;
    background: white;
    border-radius: 4px;
    color: #333333;
    ${({ theme }) => theme.bp.landscape`
        background: none;
        padding: 0;
    `}
    ${({ theme }) => theme.bp.md`
        margin-bottom: 25px;
    `}
`;

const RemoveButtonHitbox = styled.div`
    padding: 5px;
    flex-grow: 0;
`;

const RemoveButton = styled.div`
    width: 12px;
    height: 12px;
    background: var(--primary-color);
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-content: center;
    justify-content: center;
    align-items: center;
`;

const RemoveButtonMinus = styled.div`
    width: 8px;
    height: 2px;
    background: #fff;
`;

const EmptyText = styled.div`
    width: 100%;
    box-sizing: border-box;
    text-align: center;
    padding: 70px 24px 10px;
    font-size: 18px;
    font-weight: 300;
    ${({ theme }) => theme.bp.landscape`
        padding-top: 35px;
        font-size: 14px;
    `}
`;

export const SummaryItems = () => {
    const dispatch = useDispatch();
    const breakpoints = useBreakpoint();
    const summaryItems = useSelector(getSummaryItems);
    const itemStates = useSelector(getItemStates);
    const previewItem = useSelector(getPreviewItem);
    const showPrice = useSelector(getShowPrice);
    const t = useText();
    const priceFormatter = usePriceFormatter(false);

    const shouldShowVisibleIcon = (item: Item) => {
        const sameGroupSummaryItems = summaryItems.map(s => s.item).filter(i => i.group === item.group);
        if (sameGroupSummaryItems.length < 2) return false;

        if (previewItem && previewItem.group === item.group) {
            return previewItem.id === item.id;
        }
        return itemStates.isSelected(item.id);
    };

    const handleItemClick = (item: Item) => {
        const price = itemStates.getPrice(item.id);
        GaReporter.trackProductClick(item, price, "Summary items");

        dispatch({ type: PREVIEW_ITEM, item });
        if (item.preferredAngles) {
            dispatch({ type: TRIGGER_PREFERRED_ANGLE, preferredAngles: item.preferredAngles });
        }
    };

    const renderItem = (s: SummaryItem) => {
        const price = itemStates.getPrice(s.item.id);
        const priceText = price?.text ? priceFormatter.formatPrice(price.priceValue * s.amount) : null;
        return (
            <ItemRow key={s.item.id}>
                <EyeIcon
                    className={
                        shouldShowVisibleIcon(s.item) ? "visible material-icons-outlined" : "material-icons-outlined"
                    }
                >
                    remove_red_eye
                </EyeIcon>
                <ItemName onClick={() => handleItemClick(s.item)}>
                    {s.amount > 1 ? `${s.amount} x ` : null}
                    {s.summaryText}
                </ItemName>
                {showPrice && priceText ? <ItemPrice>{priceText}</ItemPrice> : null}
                <RemoveButtonHitbox
                    onClick={() => {
                        dispatch({ type: REMOVE_ITEM, item: s.item });
                        dispatch({ type: CLEAR_PREVIEW_ITEM });
                    }}
                >
                    <RemoveButton>
                        <RemoveButtonMinus />
                    </RemoveButton>
                </RemoveButtonHitbox>
            </ItemRow>
        );
    };

    let maxHeight;
    if (!breakpoints.landscape) {
        maxHeight = "100%";
    } else if (breakpoints.lg) {
        maxHeight = "300px";
    } else if (breakpoints.md) {
        maxHeight = "240px";
    } else {
        maxHeight = "180px";
    }

    // TODO: We should add another check if the window height is below 600px.
    if (window.innerHeight < 610) {
        const mHeight = window.innerHeight - 260 < 50 ? 50 : window.innerHeight - 260;
        maxHeight = `${mHeight}px`;
    }

    return (
        <>
            {summaryItems.length > 0 ? (
                <ItemContainer>
                    <CustomScrollbar maxHeight={maxHeight} dynamicHeight>
                        <Items>{summaryItems.map(renderItem)}</Items>
                    </CustomScrollbar>
                </ItemContainer>
            ) : (
                <EmptyText>{t("summaryEmpty")}</EmptyText>
            )}
        </>
    );
};
